import type { UploadProps } from "antd";
import { Col, Row } from "antd";
import {
  AvatarBlock,
  AvatarEmail,
  AvatarHint,
  AvatarInfo,
  AvatarName,
  AvatarRemoveLink,
  AvatarSpinWrapper,
  AvatarUpload,
  AvatarWrapper,
  CardHeading,
  CardSubtitle,
  CardTitle,
  EditableField,
  FormItem,
  ProfileAvatar,
  ReadonlyField,
  ReadonlyFieldLabel,
  ReadonlyValue,
  SectionCard,
} from "../../style";

interface PersonalDetailsCardProps {
  displayName: string;
  displayEmail: string;
  avatarSrc: string | null;
  avatarUploading: boolean;
  avatarRemoving: boolean;
  hasAvatar: boolean;
  onAvatarSelect: (file: File) => void;
  onAvatarRemove: () => void;
}

export default function PersonalDetailsCard({
  displayName,
  displayEmail,
  avatarSrc,
  avatarUploading,
  avatarRemoving,
  hasAvatar,
  onAvatarSelect,
  onAvatarRemove,
}: PersonalDetailsCardProps) {
  const uploadProps: UploadProps = {
    accept: "image/png,image/jpeg",
    showUploadList: false,
    maxCount: 1,
    disabled: avatarUploading || avatarRemoving,
    beforeUpload: (file) => {
      onAvatarSelect(file as File);
      return false;
    },
  };

  const initial = displayName.charAt(0).toUpperCase() || "U";

  return (
    <SectionCard>
      <CardHeading vertical gap={2}>
        <CardTitle level={3}>Personal Details</CardTitle>
        <CardSubtitle>Update your display image, name and email</CardSubtitle>
      </CardHeading>

      <Row gutter={[24, 20]} align="middle">
        <Col xs={24} lg={8}>
          <AvatarBlock align="center" gap={14}>
            <AvatarWrapper>
              <AvatarUpload {...uploadProps}>
                <ProfileAvatar size={64} src={avatarSrc ?? undefined}>
                  {initial}
                </ProfileAvatar>
              </AvatarUpload>
              {avatarUploading && <AvatarSpinWrapper size="small" />}
            </AvatarWrapper>
            <AvatarInfo vertical gap={2}>
              <AvatarName>{displayName}</AvatarName>
              <AvatarEmail>{displayEmail}</AvatarEmail>
              <AvatarHint>PNG or JPG, max 4MB.</AvatarHint>
              {hasAvatar && (
                <AvatarRemoveLink
                  disabled={avatarUploading || avatarRemoving}
                  onClick={() => {
                    if (!avatarUploading && !avatarRemoving) onAvatarRemove();
                  }}
                >
                  {avatarRemoving ? "Removing…" : "Remove photo"}
                </AvatarRemoveLink>
              )}
            </AvatarInfo>
          </AvatarBlock>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <FormItem
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: "Please enter your full name" }]}
          >
            <EditableField placeholder="Enter full name" />
          </FormItem>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <ReadonlyFieldLabel>Email</ReadonlyFieldLabel>
          <ReadonlyField aria-readonly>
            <ReadonlyValue>{displayEmail}</ReadonlyValue>
          </ReadonlyField>
        </Col>
      </Row>
    </SectionCard>
  );
}
